import type { ExistingTierPricing } from './modals/SetPricingModal';
import {
	type GetOrderQuoteOfferBatchesQuery,
	type MessageResponse,
	OrderQuoteOfferStatus,
	useAddOrderQuoteOfferMutation,
	useDeleteOrderQuoteOfferBatchMutation,
	useGetOrderQuoteOfferBatchesQuery,
	useRemoveOrderQuoteOfferAdjustmentMutation,
	useRemoveOrderQuoteOfferMutation,
	useRemoveOrderQuoteOfferSampleMutation,
	useCreateOrderQuoteGuestAcceptanceLinkMutation,
	useRecallOrderQuoteOfferBatchMutation,
	useSendOrderQuoteOfferBatchToClientMutation,
} from '@codezo/graphql';
import { FileText } from 'lucide-react';
import React, { useCallback, useState } from 'react';
import { toast } from 'sonner';
import { type AdjustmentToEdit, EditAdjustmentModal } from './modals/EditAdjustmentModal';
import { CreateOfferBatchWizard } from './modals/CreateOfferBatchWizard';
import { EditBatchModal } from './modals/EditBatchModal';
import { DeliveryEditorModal } from './modals/DeliveryEditorModal';
import { EditSampleModal } from './modals/EditSampleModal';
import { SetPricingModal } from './modals/SetPricingModal';
import { Card } from '../ui/card';
import { ConfirmDialog } from '../ui/confirm-dialog';
import { SendToClientDialog } from './SendToClientDialog';
import type { BatchLayoutProps } from './offer-components/types';

import { useCurrencyStore } from '../../stores/currency';

/* ════════════════════════ OFFERS ════════════════════════ */

interface QuoteItem {
	id: string;
	productId: string;
	productName?: string | null;
	productSku?: string | null;
	/* Frozen snapshot of the catalogue images: url + isPrimary only, and the url
	   is signed with a one-hour expiry. Render it; never persist it or key off it. */
	productImages?: Array<{ url?: string | null; isPrimary?: boolean | null }> | null;
	quantities: Array<{ id: string; quantity: number }>;
	deadline?: string | null;
}

interface OrderQuoteOffersProps {
	quoteId: string;
	quoteItems: QuoteItem[];
	/** Quote-level deadline, used as the feasibility fallback when an item has none. */
	quoteDeadline?: string | null;
	editable?: boolean;
	skip?: boolean;
	/** The quote has chosen client contacts — lets the send dialog flag a list that no longer applies. */
	hasClientContacts?: boolean;
	/**
	 * Draws one batch. A render prop rather than the layout living here: the
	 * batches query, fifteen mutations and nine confirm dialogs are the same
	 * whatever a batch looks like, and duplicating them would mean fixing every
	 * offer bug twice.
	 */
	renderBatch: (props: BatchLayoutProps) => React.ReactNode;
	/**
	 * Something here changed the quote itself, not just its offers.
	 *
	 * Nearly every mutation below does: sending or recalling a batch moves the
	 * quote's status and its lifecycle stepper, adding or deleting one moves the
	 * offer count, and any price, sample or adjustment edit moves the value
	 * range. All of that is drawn by the page above from its own queries, so
	 * refetching the batches alone leaves the header a version behind until a
	 * reload.
	 */
	onQuoteChanged?: () => void;
}

export function OrderQuoteOffers({
	quoteId,
	quoteItems,
	quoteDeadline,
	editable = true,
	skip = false,
	hasClientContacts = false,
	renderBatch,
	onQuoteChanged,
}: OrderQuoteOffersProps) {
	const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
	const [addOffersBatchId, setAddOffersBatchId] = useState<string | null>(null);
	const [sendToClientTarget, setSendToClientTarget] = useState<{
		id: string;
		name: string;
	} | null>(null);
	const [editBatch, setEditBatch] = useState<{
		id: string;
		name: string;
		acceptDeadline?: string | null;
	} | null>(null);
	const [pricingOffer, setPricingOffer] = useState<{
		offerId: string;
		quoteItemId: string;
		existingPricing: ExistingTierPricing[];
	} | null>(null);
	const [adjustmentOffer, setAdjustmentOffer] = useState<string | null>(null);
	/** The adjustment being amended. Its own state, not a mode on the one above:
	 * adding needs the offer to attach to, editing needs the row itself. */
	const [editAdjTarget, setEditAdjTarget] = useState<AdjustmentToEdit | null>(null);
	const [removeOfferTarget, setRemoveOfferTarget] = useState<{ id: string; name: string } | null>(
		null
	);
	const [removeAdjTarget, setRemoveAdjTarget] = useState<{ id: string; name: string } | null>(
		null
	);
	const [removeSampleTarget, setRemoveSampleTarget] = useState<{ id: string } | null>(null);
	// The batch whose delivery grid is being edited, plus the tier previewed per
	// product so the editor opens on the quantity being looked at.
	const [deliveryEditor, setDeliveryEditor] = useState<{
		batchId: string;
	} | null>(null);
	type OfferFromQuery =
		GetOrderQuoteOfferBatchesQuery['getOrderQuoteOfferBatches'][number]['offers'][number];
	const [sampleOffer, setSampleOffer] = useState<{
		offerId: string;
		existing: OfferFromQuery['sample'];
	} | null>(null);

	const { selectedCurrency } = useCurrencyStore();

	const { data, loading, refetch } = useGetOrderQuoteOfferBatchesQuery({
		variables: { orderQuoteId: quoteId, inCurrency: selectedCurrency },
		fetchPolicy: 'no-cache',
		skip,
	});

	/* The one refresh every handler and every modal below goes through. The
	   batches are ours; the quote's status, lifecycle and value range belong to
	   the page, and there is no mutation here that moves one without the other,
	   so nothing gets to refetch just half. */
	const refresh = useCallback(() => {
		void refetch();
		onQuoteChanged?.();
	}, [refetch, onQuoteChanged]);

	const [deleteBatch, { loading: deleting }] = useDeleteOrderQuoteOfferBatchMutation();
	const [removeAdjustment] = useRemoveOrderQuoteOfferAdjustmentMutation();
	const [removeSampleMut] = useRemoveOrderQuoteOfferSampleMutation();
	const [sendToClient, { loading: sendingToClient }] =
		useSendOrderQuoteOfferBatchToClientMutation();
	const [createGuestLink, { loading: creatingGuestLink }] =
		useCreateOrderQuoteGuestAcceptanceLinkMutation();
	const [recallBatch] = useRecallOrderQuoteOfferBatchMutation();
	const [recallTarget, setRecallTarget] = useState<{ id: string; name: string } | null>(null);
	const [_addOffer] = useAddOrderQuoteOfferMutation();
	const [removeOffer] = useRemoveOrderQuoteOfferMutation();

	// Build a lookup: quoteItemId → product info
	const itemLookup = new Map(quoteItems.map((item) => [item.id, item]));

	const batches = data?.getOrderQuoteOfferBatches ?? [];

	// Feasibility context for an offer's edit modals: the accept-by anchor (batch)
	// and the deadline to judge against (item deadline, else quote deadline).
	const findOfferContext = (offerId: string) => {
		for (const batch of batches) {
			const offer = batch.offers.find((o) => o.id === offerId);
			if (!offer) continue;
			const item = itemLookup.get(offer.orderQuoteItemId);
			return {
				offer,
				acceptDeadline: batch.acceptDeadline ?? null,
				deadline: item?.deadline ?? quoteDeadline ?? null,
			};
		}
		return null;
	};

	const pricingCtx = pricingOffer ? findOfferContext(pricingOffer.offerId) : null;
	// The sample modal's offer, for its product (catalogue prices) and supplier (which are usable).
	const sampleCtx = sampleOffer ? findOfferContext(sampleOffer.offerId) : null;

	/** Check MessageResponse and throw if not successful */
	const checkResponse = (data: unknown, mutationName: string) => {
		const res = (
			data as Record<string, Pick<MessageResponse, 'success' | 'message'> | undefined> | null
		)?.[mutationName];
		if (res && !res.success) {
			throw new Error(res.message || 'Unknown error');
		}
	};

	const handleDelete = async () => {
		if (!deleteTarget) return;
		try {
			const { data } = await deleteBatch({ variables: { id: deleteTarget.id } });
			checkResponse(data, 'deleteOrderQuoteOfferBatch');
			toast.success(`"${deleteTarget.name}" deleted`);
			setDeleteTarget(null);
			refresh();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to delete offer');
		}
	};

	const handleRemoveOffer = async () => {
		if (!removeOfferTarget) return;
		try {
			const { data } = await removeOffer({ variables: { id: removeOfferTarget.id } });
			checkResponse(data, 'removeOrderQuoteOffer');
			toast.success('Product removed');
			setRemoveOfferTarget(null);
			refresh();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to remove product');
		}
	};

	const handleRemoveSample = async () => {
		if (!removeSampleTarget) return;
		try {
			const { data } = await removeSampleMut({ variables: { id: removeSampleTarget.id } });
			checkResponse(data, 'removeOrderQuoteOfferSample');
			toast.success('Sample removed');
			setRemoveSampleTarget(null);
			refresh();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to remove sample');
		}
	};

	const handleRemoveAdjustment = async () => {
		if (!removeAdjTarget) return;
		try {
			const { data } = await removeAdjustment({ variables: { id: removeAdjTarget.id } });
			checkResponse(data, 'removeOrderQuoteOfferAdjustment');
			toast.success('Adjustment removed');
			setRemoveAdjTarget(null);
			refresh();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to remove adjustment');
		}
	};

	const handleRecall = async () => {
		if (!recallTarget) return;
		try {
			const { data } = await recallBatch({ variables: { id: recallTarget.id } });
			checkResponse(data, 'recallOrderQuoteOfferBatch');
			toast.success(`"${recallTarget.name}" recalled`);
			setRecallTarget(null);
			refresh();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to recall offer');
		}
	};

	const handleSendToClient = async (guestEmail?: string) => {
		if (!sendToClientTarget) return;
		const batchId = sendToClientTarget.id;
		try {
			const { data } = await sendToClient({ variables: { id: batchId } });
			checkResponse(data, 'sendOrderQuoteOfferBatchToClient');
			toast.success(`"${sendToClientTarget.name}" sent to client`);
			setSendToClientTarget(null);

			// Chained after the send, not alongside it: a link can be issued at any
			// point, but a failure to mint one must never read as a failed send, so
			// it gets its own try and its own message.
			if (guestEmail) {
				try {
					const { data: linkData } = await createGuestLink({
						variables: { input: { orderQuoteId: quoteId, email: guestEmail } },
					});
					const res = linkData?.createOrderQuoteGuestAcceptanceLink;
					if (!res?.success) {
						throw new Error(res?.message || 'Unknown error');
					}
					toast.success(`Acceptance link sent to ${res.email ?? guestEmail}`);
				} catch (err) {
					toast.error(
						err instanceof Error
							? `Sent, but the guest link failed: ${err.message}`
							: 'Sent, but the guest link failed'
					);
				}
			}

			refresh();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to send to client');
		}
	};

	if (loading && batches.length === 0) {
		return (
			<Card className="overflow-hidden border-line-1 bg-surface gap-0 py-0">
				{Array.from({ length: 3 }).map((_, i) => (
					<div
						key={i}
						className="flex items-center gap-4 px-4 py-4"
						style={{ borderBottom: i < 2 ? '0.5px solid var(--line-1)' : 'none' }}
					>
						<div className="h-5 w-32 animate-pulse rounded bg-surface-fill" />
						<div className="h-4 w-20 animate-pulse rounded bg-surface-fill" />
						<div className="ml-auto h-4 w-16 animate-pulse rounded bg-surface-fill" />
					</div>
				))}
			</Card>
		);
	}

	return (
		<div>
			{batches.length === 0 ? (
				<Card className="border-line-1 bg-surface py-16">
					<div className="flex flex-col items-center gap-3 text-center">
						<div className="flex h-11 w-11 items-center justify-center rounded-full border border-line-1 bg-surface-fill text-text-3">
							<FileText size={20} />
						</div>
						<div className="text-base text-text-2">No offers yet</div>
						<div className="text-sm text-text-4">
							{/* Every quote gets its offer when it's created, so this is only a
							    quote from before that with nothing to backfill. */}
							{editable
								? 'Add products to the quote to start pricing them.'
								: 'This quote is no longer editable.'}
						</div>
					</div>
				</Card>
			) : (
				<div className="flex flex-col gap-3">
					{batches.map((batch) => {
						const cardProps: BatchLayoutProps = {
							batch,
							isAccepted: batch.status === OrderQuoteOfferStatus.Accepted,
							itemLookup,
							quoteDeadline,
							selectedCurrency,
							onChanged: refresh,
							setAddOffersBatchId,
							setEditBatch,
							setSendToClientTarget,
							setRecallTarget,
							setDeleteTarget,
							setPricingOffer,
							setSampleOffer,
							setAdjustmentOffer,
							setEditAdjTarget,
							setRemoveOfferTarget,
							setRemoveSampleTarget,
							setRemoveAdjTarget,
							setDeliveryEditor,
						};
						return (
							<React.Fragment key={batch.id}>{renderBatch(cardProps)}</React.Fragment>
						);
					})}
				</div>
			)}

			<SendToClientDialog
				open={!!sendToClientTarget}
				onOpenChange={(v) => !v && setSendToClientTarget(null)}
				quoteId={quoteId}
				hasClientContacts={hasClientContacts}
				target={sendToClientTarget}
				loading={sendingToClient || creatingGuestLink}
				onConfirm={handleSendToClient}
			/>

			<ConfirmDialog
				open={!!recallTarget}
				onOpenChange={(v) => !v && setRecallTarget(null)}
				title="Recall offer"
				description={`Are you sure you want to recall "${recallTarget?.name}"? The client will no longer be able to see it.`}
				confirmLabel="Recall"
				onConfirm={handleRecall}
			/>

			<ConfirmDialog
				open={!!deleteTarget}
				onOpenChange={(v) => !v && setDeleteTarget(null)}
				title="Delete offer"
				description={`Are you sure you want to delete "${deleteTarget?.name}"? This will remove all products in this offer.`}
				confirmLabel="Delete"
				loading={deleting}
				onConfirm={handleDelete}
			/>

			<ConfirmDialog
				open={!!removeOfferTarget}
				onOpenChange={(v) => !v && setRemoveOfferTarget(null)}
				title="Remove product"
				description={`Are you sure you want to remove "${removeOfferTarget?.name}" from this offer?`}
				confirmLabel="Remove"
				onConfirm={handleRemoveOffer}
			/>

			<ConfirmDialog
				open={!!removeSampleTarget}
				onOpenChange={(v) => !v && setRemoveSampleTarget(null)}
				title="Remove sample"
				description="Are you sure you want to remove the sample from this offer?"
				confirmLabel="Remove"
				onConfirm={handleRemoveSample}
			/>

			<ConfirmDialog
				open={!!removeAdjTarget}
				onOpenChange={(v) => !v && setRemoveAdjTarget(null)}
				title="Remove adjustment"
				description={`Are you sure you want to remove "${removeAdjTarget?.name}"?`}
				confirmLabel="Remove"
				onConfirm={handleRemoveAdjustment}
			/>

			{addOffersBatchId && (
				<CreateOfferBatchWizard
					open={!!addOffersBatchId}
					onOpenChange={(v) => !v && setAddOffersBatchId(null)}
					quoteId={quoteId}
					quoteItems={quoteItems}
					quoteDeadline={quoteDeadline}
					existingBatchId={addOffersBatchId}
					existingOfferItemIds={
						batches
							.find((b) => b.id === addOffersBatchId)
							?.offers.map((o) => o.orderQuoteItemId) ?? []
					}
					onSuccess={refresh}
				/>
			)}

			{editBatch && (
				<EditBatchModal
					open={!!editBatch}
					onOpenChange={(v) => !v && setEditBatch(null)}
					batchId={editBatch.id}
					initialValues={editBatch}
					onSuccess={refresh}
				/>
			)}

			{pricingOffer && (
				<SetPricingModal
					open={!!pricingOffer}
					onOpenChange={(v) => !v && setPricingOffer(null)}
					offerId={pricingOffer.offerId}
					quantities={itemLookup.get(pricingOffer.quoteItemId)?.quantities ?? []}
					existingPricing={pricingOffer.existingPricing}
					productId={itemLookup.get(pricingOffer.quoteItemId)?.productId ?? ''}
					vetterEntityId={pricingCtx?.offer.vetterEntityId ?? null}
					vetterName={pricingCtx?.offer.vetterEntity?.name ?? null}
					deadline={pricingCtx?.deadline ?? null}
					acceptDeadline={pricingCtx?.acceptDeadline ?? null}
					onSuccess={refresh}
				/>
			)}

			{(adjustmentOffer || editAdjTarget) && (
				<EditAdjustmentModal
					open={!!adjustmentOffer || !!editAdjTarget}
					onOpenChange={(v) => {
						if (v) return;
						setAdjustmentOffer(null);
						setEditAdjTarget(null);
					}}
					// Only ever used when authoring; an edit addresses the row by id.
					offerId={adjustmentOffer ?? ''}
					existing={editAdjTarget}
					onSuccess={refresh}
				/>
			)}

			{sampleOffer && (
				<EditSampleModal
					open={!!sampleOffer}
					onOpenChange={(v) => !v && setSampleOffer(null)}
					offerId={sampleOffer.offerId}
					productId={
						itemLookup.get(sampleCtx?.offer.orderQuoteItemId ?? '')?.productId ?? null
					}
					vetterEntityId={sampleCtx?.offer.vetterEntityId ?? null}
					existing={sampleOffer.existing}
					onSuccess={refresh}
				/>
			)}

			{deliveryEditor &&
				(() => {
					const batch = batches.find((b) => b.id === deliveryEditor.batchId);
					if (!batch) return null;
					return (
						<DeliveryEditorModal
							open
							onOpenChange={(v) => !v && setDeliveryEditor(null)}
							batch={batch}
							itemLookup={itemLookup}
							quoteDeadline={quoteDeadline ?? null}
							onSuccess={refresh}
						/>
					);
				})()}
		</div>
	);
}
